import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Download, Eye, Trash2, X } from 'lucide-react'
import { recruitmentAdminApi } from '../../../services/api'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import { formatDateTime } from '../../../utils/date'
import AdminFormField, { adminInputClass } from '../../../components/admin/AdminFormField'
import { useMarkNotificationsRead } from '../../../hooks/useMarkNotificationsRead'
import { useLanguage } from '../../../context/LanguageContext'

export default function AdminJobApplicationDetail() {
  const { id } = useParams()
  const { t } = useTranslation()
  const { language } = useLanguage()
  useMarkNotificationsRead('recruitment_applications', id ? Number(id) : null)
  const [application, setApplication] = useState(null)
  const [statuses, setStatuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusId, setStatusId] = useState('')
  const [note, setNote] = useState('')
  const [adminNotes, setAdminNotes] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [cvPreview, setCvPreview] = useState(null)

  const load = () => {
    setLoading(true)
    Promise.all([
      recruitmentAdminApi.applications.get(id),
      recruitmentAdminApi.statuses.getAll(),
    ])
      .then(([appRes, statusRes]) => {
        const app = appRes.data.data || appRes.data
        setApplication(app)
        setStatusId(String(app.status_id))
        setAdminNotes(app.admin_notes || '')
        setStatuses(statusRes.data.data || statusRes.data || [])
      })
      .finally(() => setLoading(false))
  }

  useEffect(load, [id])

  const departmentName = () => {
    const dept = application?.department
    if (!dept) return '—'
    return language === 'ar' ? dept.name_ar || dept.name_en : dept.name_en || dept.name_ar
  }

  const specializationName = () => {
    const spec = application?.specialization
    if (!spec) return '—'
    return language === 'ar' ? spec.name_ar || spec.name_en : spec.name_en || spec.name_ar
  }

  const handleStatusUpdate = async () => {
    setSaving(true)
    try {
      await recruitmentAdminApi.applications.updateStatus(id, {
        status_id: Number(statusId),
        admin_notes: adminNotes,
      })
      load()
    } finally {
      setSaving(false)
    }
  }

  const handleAddNote = async () => {
    if (!note.trim()) return
    await recruitmentAdminApi.applications.addNote(id, { note })
    setNote('')
    load()
  }

  const handleSendEmail = async () => {
    if (!emailMessage.trim()) return
    await recruitmentAdminApi.applications.sendEmail(id, { message: emailMessage })
    setEmailMessage('')
  }

  const handleDownload = async (attachmentId, filename) => {
    const { data } = await recruitmentAdminApi.applications.downloadAttachment(id, attachmentId)
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
  }

  const handleViewCv = async (file) => {
    const { data } = await recruitmentAdminApi.applications.viewAttachment(id, file.id)
    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
    setCvPreview({ url, name: file.original_name })
  }

  const closeCvPreview = () => {
    if (cvPreview?.url) window.URL.revokeObjectURL(cvPreview.url)
    setCvPreview(null)
  }

  const handleDelete = async () => {
    if (!window.confirm(t('admin.recruitment.confirmDeleteApplication'))) return
    await recruitmentAdminApi.applications.delete(id)
    window.location.href = '/cp-login/recruitment/applications'
  }

  if (loading) return <LoadingSpinner className="py-20" />
  if (!application) return null

  const cvFile = application.cv_attachment || application.attachments?.find((file) => file.type === 'cv')

  return (
    <div>
      <Link to="/cp-login/recruitment/applications" className="mb-6 inline-flex items-center gap-2 text-sm text-primary">
        <ArrowLeft className="h-4 w-4" />
        {t('admin.recruitment.backToApplications')}
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary">{application.full_name}</h2>
          <p className="text-text/60">{application.reference_number}</p>
        </div>
        <Button variant="ghost" onClick={handleDelete} className="text-red-600">
          <Trash2 className="h-4 w-4" />
          {t('buttons.delete')}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <h3 className="mb-4 font-semibold text-primary">{t('admin.recruitment.applicantInfo')}</h3>
            <dl className="grid gap-3 sm:grid-cols-2 text-sm">
              <div>
                <dt className="text-text/50">{t('careers.form.email')}</dt>
                <dd className="font-medium">{application.email}</dd>
              </div>
              <div>
                <dt className="text-text/50">{t('careers.form.phone')}</dt>
                <dd className="font-medium">{application.phone}</dd>
              </div>
              <div>
                <dt className="text-text/50">{t('admin.recruitment.department')}</dt>
                <dd className="font-medium">{departmentName()}</dd>
              </div>
              <div>
                <dt className="text-text/50">{t('admin.recruitment.specialization')}</dt>
                <dd className="font-medium">{specializationName()}</dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h3 className="mb-4 font-semibold text-primary">{t('admin.recruitment.attachments')}</h3>
            {cvFile ? (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleViewCv(cvFile)}
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-3 text-sm hover:bg-primary/5"
                >
                  <Eye className="h-4 w-4 text-primary" />
                  {t('admin.recruitment.viewCv')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(cvFile.id, cvFile.original_name)}
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-3 text-sm hover:bg-primary/5"
                >
                  <Download className="h-4 w-4 text-primary" />
                  {cvFile.original_name}
                </button>
              </div>
            ) : (
              <p className="text-sm text-text/60">{t('admin.table.noData')}</p>
            )}
          </Card>

          <Card>
            <h3 className="mb-4 font-semibold text-primary">{t('admin.recruitment.statusHistory')}</h3>
            <div className="space-y-3">
              {(application.status_logs || []).map((log) => (
                <div key={log.id} className="border-s-2 border-primary/20 ps-4 text-sm">
                  <div className="font-medium">
                    {log.from_status?.name_en || '—'} → {log.to_status?.name_en}
                  </div>
                  <div className="text-text/50">
                    {log.user?.name || 'System'} · {formatDateTime(log.created_at)}
                  </div>
                  {log.note && <p className="mt-1 text-text/70">{log.note}</p>}
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <h3 className="mb-4 font-semibold text-primary">{t('admin.recruitment.departmentApplied')}</h3>
            <p className="font-medium">{departmentName()}</p>
            <p className="mt-1 text-sm text-text/60">{formatDateTime(application.created_at)}</p>
          </Card>

          <Card>
            <h3 className="mb-4 font-semibold text-primary">{t('admin.recruitment.changeStatus')}</h3>
            <AdminFormField label={t('admin.recruitment.status')} htmlFor="application-status" className="mb-3">
              <select
                id="application-status"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
                className={adminInputClass}
              >
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.name_en}</option>
                ))}
              </select>
            </AdminFormField>
            <AdminFormField label={t('admin.recruitment.adminNotes')} htmlFor="application-admin-notes" className="mb-3">
              <textarea
                id="application-admin-notes"
                rows={3}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className={adminInputClass}
              />
            </AdminFormField>
            <Button variant="primary" onClick={handleStatusUpdate} disabled={saving} className="w-full">
              {t('admin.recruitment.updateStatus')}
            </Button>
          </Card>

          <Card>
            <h3 className="mb-4 font-semibold text-primary">{t('admin.recruitment.addNote')}</h3>
            <AdminFormField label={t('admin.recruitment.fields.note')} htmlFor="application-note" className="mb-3">
              <textarea
                id="application-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className={adminInputClass}
              />
            </AdminFormField>
            <Button variant="outlineDark" onClick={handleAddNote} className="w-full">
              {t('admin.recruitment.saveNote')}
            </Button>
          </Card>

          <Card>
            <h3 className="mb-4 font-semibold text-primary">{t('admin.recruitment.sendEmail')}</h3>
            <AdminFormField label={t('admin.recruitment.fields.emailMessage')} htmlFor="application-email" className="mb-3">
              <textarea
                id="application-email"
                rows={4}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className={adminInputClass}
              />
            </AdminFormField>
            <Button variant="accent" onClick={handleSendEmail} className="w-full">
              {t('admin.recruitment.sendEmail')}
            </Button>
          </Card>

          {(application.notes || []).length > 0 && (
            <Card>
              <h3 className="mb-4 font-semibold text-primary">{t('admin.recruitment.internalNotes')}</h3>
              <div className="space-y-3">
                {application.notes.map((item) => (
                  <div key={item.id} className="rounded-xl bg-primary/5 p-3 text-sm">
                    <p>{item.note}</p>
                    <p className="mt-1 text-xs text-text/50">{item.user?.name} · {formatDateTime(item.created_at)}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {cvPreview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="font-semibold text-primary">{cvPreview.name}</p>
              <button type="button" onClick={closeCvPreview} className="rounded-lg p-2 hover:bg-primary/5">
                <X className="h-5 w-5" />
              </button>
            </div>
            <iframe title={cvPreview.name} src={cvPreview.url} className="h-full w-full" />
          </div>
        </div>
      ) : null}
    </div>
  )
}
